import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';

/**
 * Who is signed in, in the terms analytics reports on.
 *
 * Never carries a name or an email address. Google prohibits personally
 * identifiable information in Analytics and can delete a property's data over
 * it, so the identity here is the Firebase uid plus the organisation and role
 * claims - enough to segment by tenant and permission level, and nothing that
 * identifies a person.
 */
export interface AnalyticsContext {
    user_id: string;
    organization_id: string;
    user_role: string;
    org_type: string;
}

const ANONYMOUS: AnalyticsContext = {
    user_id: 'anonymous',
    organization_id: 'unknown',
    user_role: 'unknown',
    org_type: 'unknown'
};

@Injectable({ providedIn: 'root' })
export class AnalyticsContextService {
    private auth = inject(Auth);

    /**
     * Cached so a burst of errors does not trigger a token read each time.
     * Keyed by uid, so it drops automatically when the user changes.
     */
    private cache?: { uid: string; context: AnalyticsContext; at: number };
    private readonly CACHE_MS = 60_000;

    /**
     * Resolve the current analytics context, falling back to anonymous rather
     * than throwing - telemetry must never break the path it is reporting on.
     */
    async get(): Promise<AnalyticsContext> {
        const user = this.auth.currentUser;
        if (!user) {
            return ANONYMOUS;
        }

        const cached = this.cache;
        if (cached && cached.uid === user.uid && Date.now() - cached.at < this.CACHE_MS) {
            return cached.context;
        }

        try {
            const token = await user.getIdTokenResult();
            const claims = token.claims;

            // `orgId` is the claim actually minted by setOrgClaims(); an
            // earlier `organizationId` was read here but never written
            // anywhere, so every event reported organization_id: 'unknown'.
            // It stays in the fallback chain only for tokens minted before a
            // refresh.
            const context: AnalyticsContext = {
                user_id: user.uid,
                organization_id: (claims['orgId'] as string)
                    || (claims['organizationId'] as string)
                    || 'unknown',
                user_role: (claims['platformAdmin'] ? 'platform_admin' : (claims['role'] as string)) || 'unknown',
                org_type: (claims['orgType'] as string) || 'unknown'
            };

            this.cache = { uid: user.uid, context, at: Date.now() };
            return context;
        } catch {
            // Token unavailable (offline, revoked). Report what we do know.
            return { ...ANONYMOUS, user_id: user.uid };
        }
    }

    /** Drop the cache, e.g. after claims are refreshed following an org change. */
    invalidate(): void {
        this.cache = undefined;
    }
}
